import { useState } from 'react';

import { useGlobalContext } from '../../../context/global';
import { type IPresenceAnswer, type IPresenceTally, markRemainingAbsent } from './agent-presence';
import type { PresenceReadStatus } from './use-agent-presence';

/**
 * The polling-agent summary line + the one bulk action.
 *
 * "Mark the rest as no agent" only fills blanks, so it is safe at any time. Undo stays on
 * offer until the agent changes anything else — it restores the exact array from before.
 */
export const PresenceBulkBar = ({
	rosterIds,
	tally,
	readStatus,
}: {
	rosterIds: number[];
	tally: IPresenceTally;
	readStatus?: PresenceReadStatus;
}) => {
	const {
		global: {
			state: { agentPresence },
			updateState,
		},
	} = useGlobalContext();
	const [undo, setUndo] = useState<{ before: IPresenceAnswer[]; after: IPresenceAnswer[]; count: number } | null>(null);

	// Any other edit invalidates the undo — it would otherwise roll that edit back too.
	const undoable = undo?.after === agentPresence ? undo : null;

	const markRest = () => {
		const { next, changed } = markRemainingAbsent(agentPresence, rosterIds);
		if (!changed.length) return;
		updateState('agentPresence', next);
		setUndo({ before: agentPresence, after: next, count: changed.length });
	};

	const onUndo = () => {
		if (!undoable) return;
		updateState('agentPresence', undoable.before);
		setUndo(null);
	};

	return (
		<div className='pa-bulk'>
			<div className='pa-bulk__copy'>
				<p className='pa-bulk__title'>Polling agents</p>
				<p className='pa-bulk__text'>
					For each party, check column 4 of the sheet: was the agent&apos;s name or signature there?
					{readStatus === 'loaded' ? ' Answers already saved for this unit are filled in.' : ''}
					{readStatus === 'unavailable' ? ' Saved answers could not be loaded; they will be kept when you submit.' : ''}
				</p>
				<p
					className='pa-bulk__tally'
					aria-live='polite'
				>
					<span className='pa-bulk__chip pa-bulk__chip--present'>{tally.present} present</span>
					<span className='pa-bulk__chip pa-bulk__chip--absent'>{tally.absent} no agent</span>
					<span className='pa-bulk__chip pa-bulk__chip--unrecorded'>{tally.unrecorded} not recorded</span>
				</p>
			</div>
			{undoable ? (
				<button
					type='button'
					className='pa-bulk__action pa-bulk__action--ghost'
					onClick={onUndo}
				>
					Undo ({undoable.count} marked)
				</button>
			) : tally.unrecorded > 0 ? (
				<button
					type='button'
					className='pa-bulk__action'
					onClick={markRest}
				>
					Mark the rest as no agent
				</button>
			) : null}
		</div>
	);
};

export default PresenceBulkBar;
